/**
 * Storage configuration matching backend limits
 */

export const STORAGE_CONFIG = {
  // File size limits (matching backend)
  maxFileSize: {
    avatar: 5 * 1024 * 1024,    // 5MB for avatars
    document: 50 * 1024 * 1024, // 50MB for documents
    attachment: 10 * 1024 * 1024, // 10MB for attachments
    export: 100 * 1024 * 1024,  // 100MB for exports
  },

  // Allowed MIME types per purpose
  allowedMimeTypes: {
    avatar: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
    ],
    document: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/plain',
      'text/csv',
    ],
    attachment: [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/pdf',
      'text/plain',
    ],
    export: [
      'application/json',
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ],
  },

  // File extension mappings
  extensionToMimeType: {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    pdf: 'application/pdf',
    doc: 'application/msword',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    xls: 'application/vnd.ms-excel',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    txt: 'text/plain',
    csv: 'text/csv',
    json: 'application/json',
  },

  // Upload expiry time (matching backend)
  uploadExpiryMinutes: 15,

  // Download expiry time (matching backend)
  downloadExpiryMinutes: 60,
} as const;

/**
 * Get the maximum file size for a given purpose
 */
export function getMaxFileSize(purpose: 'avatar' | 'document' | 'attachment' | 'export'): number {
  return STORAGE_CONFIG.maxFileSize[purpose];
}

/**
 * Check if a MIME type is allowed for a given purpose
 */
export function isAllowedMimeType(purpose: 'avatar' | 'document' | 'attachment' | 'export', mimeType: string): boolean {
  const allowed: readonly string[] = STORAGE_CONFIG.allowedMimeTypes[purpose];
  return allowed.includes(mimeType);
}

/**
 * Get MIME type from file extension
 */
export function getMimeTypeFromExtension(filename: string): string | undefined {
  const extension = filename.split('.').pop()?.toLowerCase();
  return extension ? STORAGE_CONFIG.extensionToMimeType[extension as keyof typeof STORAGE_CONFIG.extensionToMimeType] : undefined;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Validate file before upload
 */
export function validateFile(
  file: File,
  purpose: 'avatar' | 'document' | 'attachment' | 'export'
): { valid: boolean; error?: string } {
  // Check for zero-byte files
  if (file.size === 0) {
    return {
      valid: false,
      error: 'Cannot upload empty files. Please select a valid file.'
    };
  }
  
  const maxSize = getMaxFileSize(purpose);
  
  if (file.size > maxSize) {
    return { 
      valid: false, 
      error: `File size exceeds limit of ${formatFileSize(maxSize)}` 
    };
  }

  if (!isAllowedMimeType(purpose, file.type)) {
    return { 
      valid: false, 
      error: `File type ${file.type} is not allowed for ${purpose}` 
    };
  }

  return { valid: true };
}